import {
  ek21Live,
  jdLive,
  siteLive,
  summarizeFleetLive,
  utLive,
  wootalkLive,
  type LiveRead,
} from '../../../../packages/fleet-client/src/live.js';

/** 心跳超過這段時間視為過期。與 Worker `nodesAlive` 的 10 分鐘窗同一條線：剛好 10 分鐘仍算活著。 */
export const FLEET_NODE_STALE_AFTER_MS = 10 * 60 * 1000;

export { summarizeFleetLive };
export type { LiveRead };

/** D1 `datetime('now')` 是 UTC 的 `YYYY-MM-DD HH:MM:SS`，沒有時區。Date.parse 會把這種字串當本地時間。 */
const SQLITE_UTC_STAMP = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;

export type FleetNode = {
  nodeId: string;
  host: string;
  lastSeenAt: string;
  services: Record<string, unknown>;
  live: Record<string, unknown>;
  liveStatus: LiveRead;
  meta: Record<string, unknown>;
  deploySha: string | null;
  deployLabel: string | null;
  stale: boolean;
};

export type FleetNodeWire = {
  nodeId: string;
  host: string | null;
  services: Record<string, unknown>;
  live?: unknown;
  meta: Record<string, unknown>;
  lastSeenAt: string;
};

function fresh(nowMs: number, ageMs: number): string {
  return new Date(nowMs - ageMs).toISOString();
}

export function seedFleetNodes(nowMs: number): FleetNodeWire[] {
  return [
    {
      nodeId: 'kg-wootalk',
      host: 'kg',
      services: { wootalk: 'up' },
      live: wootalkLive({
        attempts: 2,
        ok: 1,
        throttled: 0,
        budgetLeft: 1,
        proxyDegraded: false,
        matches: 4,
      }),
      meta: { deploySha: 'abc1234deadbeef', deployLabel: 'wootalk@abc1234' },
      lastSeenAt: fresh(nowMs, 60_000),
    },
    {
      nodeId: 'kg-jd',
      host: 'kg',
      services: { jd: 'up' },
      live: jdLive({ alert: false, alertReason: '', authFails: 0, lastInboundTs: null }),
      meta: { deploySha: 'fff9990cafebabe' },
      lastSeenAt: fresh(nowMs, 20 * 60_000),
    },
  ];
}

function seededPair(nowMs: number): [FleetNodeWire, FleetNodeWire] {
  const nodes = seedFleetNodes(nowMs);
  const talk = nodes[0];
  const jd = nodes[1];
  if (!talk || !jd) throw new Error('fleet seed');
  return [talk, jd];
}

function utFixtureLive() {
  const live = utLive({
    rooms: [
      { id: '11124', name: '主房', online: 12 },
      { id: '53098', name: '次房', online: 3 },
    ],
    claimedIds: ['11124'],
    uncoveredIds: ['53098'],
  });
  return {
    ...live,
    rooms: live.rooms.map((room, index) =>
      index === 0 ? { ...room, sessionId: 'mock-ut-11124' } : room,
    ),
  };
}

export function fleetNodeFixture(name: string, nowMs: number): FleetNodeWire[] {
  const sha = { deploySha: 'abc1234deadbeef', deployLabel: 'fixture@abc1234' };
  const seen = fresh(nowMs, 30_000);
  const ut = {
    nodeId: 'kg-ut',
    host: 'kg',
    services: { ut: 'up' },
    live: utFixtureLive(),
    meta: sha,
    lastSeenAt: seen,
  };
  const ek21 = {
    nodeId: 'kg-ek21',
    host: 'kg',
    services: { ek21: 'up' },
    live: ek21Live([
      { id: 'chat1', online: 4, real: 2 },
      { id: 'chat3', online: 1, real: 0, empty: true },
    ]),
    meta: sha,
    lastSeenAt: seen,
  };
  const [talk, jdSeed] = seededPair(nowMs);
  const jd = { ...jdSeed, lastSeenAt: seen };
  const site = {
    nodeId: 'kg-site',
    host: 'kg',
    services: { site: 'idle' },
    live: siteLive(false),
    meta: sha,
    lastSeenAt: seen,
  };
  switch (name) {
    case 'ut':
      return [ut];
    case 'ek21':
      return [ek21];
    case 'wootalk':
      return [talk];
    case 'site':
      return [site];
    case 'jd':
      return [jd];
    case 'bad':
      return [
        {
          nodeId: 'kg-bad',
          host: 'kg',
          services: { ut: 'up' },
          live: 'not-json',
          meta: sha,
          lastSeenAt: seen,
        },
      ];
    case 'missing':
      return [
        {
          nodeId: 'kg-missing',
          host: 'kg',
          services: { site: 'up' },
          meta: sha,
          lastSeenAt: seen,
        },
      ];
    case 'stale':
      return [
        {
          ...ut,
          nodeId: 'kg-ut-stale',
          lastSeenAt: fresh(nowMs, 20 * 60_000),
        },
      ];
    case 'all':
      return [
        ut,
        ek21,
        talk,
        site,
        jd,
        {
          nodeId: 'kg-bad',
          host: 'kg',
          services: { ut: 'up' },
          live: 'not-json',
          meta: sha,
          lastSeenAt: seen,
        },
        {
          nodeId: 'kg-missing',
          host: 'kg',
          services: { site: 'up' },
          meta: sha,
          lastSeenAt: seen,
        },
        {
          ...ut,
          nodeId: 'kg-ut-stale',
          lastSeenAt: fresh(nowMs, 20 * 60_000),
        },
      ];
    default:
      return seedFleetNodes(nowMs);
  }
}

export function nodeIsStale(lastSeenAt: string, nowMs: number): boolean {
  const sqliteUtc = SQLITE_UTC_STAMP.exec(lastSeenAt);
  const seen = Date.parse(sqliteUtc ? `${sqliteUtc[1]}T${sqliteUtc[2]}Z` : lastSeenAt);
  if (!Number.isFinite(seen)) return true;
  return nowMs - seen > FLEET_NODE_STALE_AFTER_MS;
}

export function countAliveNodes(nowMs: number): number {
  return seedFleetNodes(nowMs).filter((node) => !nodeIsStale(node.lastSeenAt, nowMs)).length;
}

function readLive(value: unknown): { live: Record<string, unknown>; liveStatus: LiveRead } {
  if (value === undefined || value === null) return { live: {}, liveStatus: 'missing' };
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return { live: {}, liveStatus: 'missing' };
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return { live: parsed as Record<string, unknown>, liveStatus: 'ok' };
      }
      return { live: {}, liveStatus: 'bad' };
    } catch {
      return { live: {}, liveStatus: 'bad' };
    }
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return { live: value as Record<string, unknown>, liveStatus: 'ok' };
  }
  return { live: {}, liveStatus: 'bad' };
}

function jsonObject(value: unknown): Record<string, unknown> {
  let parsed = value;
  if (typeof parsed === 'string') {
    const trimmed = parsed.trim();
    if (trimmed === '') return {};
    try {
      parsed = JSON.parse(trimmed) as unknown;
    } catch {
      return {};
    }
  }
  if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  return {};
}

function textField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function parseOne(row: unknown, nowMs: number): FleetNode | null {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return null;
  const record = row as Record<string, unknown>;
  const nodeId = textField(record, 'nodeId');
  if (nodeId === null) return null;
  const host = textField(record, 'host') ?? '';
  const lastSeenAt = textField(record, 'lastSeenAt') ?? '';
  const meta = jsonObject(record.meta);
  const live = readLive(record.live);
  return {
    nodeId,
    host,
    lastSeenAt,
    services: jsonObject(record.services),
    live: live.live,
    liveStatus: live.liveStatus,
    meta,
    deploySha: textField(meta, 'deploySha'),
    deployLabel: textField(meta, 'deployLabel'),
    stale: nodeIsStale(lastSeenAt, nowMs),
  };
}

/** Worker 今天把 `services`／`live`／`meta` 當 JSON 字串欄回傳。物件也收，mock 走物件。 */
export function parseFleetNodes(payload: unknown, nowMs: number): FleetNode[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const nodes = (payload as { nodes?: unknown }).nodes;
  if (!Array.isArray(nodes)) return [];
  const parsed: FleetNode[] = [];
  for (const row of nodes) {
    const node = parseOne(row, nowMs);
    if (node) parsed.push(node);
  }
  return parsed;
}

export function fleetNodeServiceKeys(services: Record<string, unknown>): string[] {
  return Object.keys(services).sort();
}
